/**
 * Pyrene Newsletter
 * -----------------
 * Logique partagée entre le popup (pyrene-newsletter-popup.liquid) et le
 * formulaire footer (footer-pyrene-custom.liquid). Les deux POSTent vers
 * le même formulaire Brevo, avec SOURCE différenciée ("popup" / "footer").
 *
 * Architecture : frontend-only, submission directe vers Brevo Forms.
 * Cf. newsletter-popup-spec.md (V2) pour la spec complète.
 */

(function () {
  'use strict';

  // === Configuration globale ===

  /**
   * URL publique du formulaire Brevo (sibforms.com).
   * Sert de fallback global : le popup peut l'override via data-brevo-url
   * (Theme Editor), le footer utilise cette constante directement.
   */
  const BREVO_FORM_URL = 'https://5b7d01ac.sibforms.com/serve/MUIFAKEOINrdpuhX7zYic62keZacPZHecPgAZzsr1S4adgOJ-IRrpvhhR2zmzFBaFA2-WbpoyiEIqYUUlrajdVwga6IC1tnLYDe1jgMyJOCEDKKzJ9v6g7YFZYHiK4TwBAUM4_ba_m_IOhqPplfJ3JZk0zjJJVQnfkfSVHO3jDleIXCxiI6ys4ClEYLnEXqyRN2uexOHj7L3_Dp2';

  const STORAGE_KEY = 'pyrene_newsletter_state';

  // Pages où le popup ne doit JAMAIS s'afficher.
  const EXCLUDED_PATHS = [
    /^\/cart/,
    /^\/checkout/,
    /^\/account/,
    /^\/pages\/inscription-confirmee/,
    /^\/pages\/politique-de-confidentialite/,
  ];

  // === Utilitaires ===

  const isExcludedPath = () =>
    EXCLUDED_PATHS.some((re) => re.test(window.location.pathname));

  const isMobile = () => window.matchMedia('(max-width: 768px)').matches;

  const readState = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      return parsed;
    } catch (_e) {
      return null;
    }
  };

  const writeState = (status, cooldownDays) => {
    const until = new Date();
    until.setDate(until.getDate() + cooldownDays);
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ status, until: until.toISOString() }),
      );
    } catch (_e) {
      // localStorage indisponible (navigation privée stricte) : on accepte.
    }
  };

  const isCooldownActive = () => {
    const state = readState();
    if (!state || !state.until) return false;
    return new Date(state.until).getTime() > Date.now();
  };

  const isValidEmail = (value) => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (trimmed.length < 5 || trimmed.length > 254) return false;
    // Regex pragmatique RFC 5322 simplifiée
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  };

  /**
   * Submit générique vers Brevo Forms.
   * Mode no-cors car sibforms.com n'expose pas Access-Control-Allow-Origin
   * pour les origines arbitraires. On ne peut pas lire la réponse, mais
   * la requête est bien transmise et traitée côté Brevo (qui enverra
   * l'email DOI). On considère succès dès lors qu'aucune exception réseau.
   *
   * @param {string} url        URL Brevo Forms (sibforms.com/serve/...)
   * @param {string} email      Adresse email du visiteur
   * @param {string} source     Origine de l'inscription : "popup" ou "footer"
   * @returns {Promise<void>}
   */
  const submitToBrevo = async (url, email, source) => {
    const body = new URLSearchParams();
    body.append('EMAIL', email);
    body.append('email_address_check', '');
    body.append('locale', 'fr');
    body.append('SOURCE', source);

    await fetch(url, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  };

  // === Classe principale ===

  class PyreneNewsletterPopup {
    /** @param {HTMLElement} root */
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('.pyrene-np__dialog');
      this.form = root.querySelector('[data-pyrene-np-form]');
      this.submitBtn = root.querySelector('[data-pyrene-np-submit]');
      this.successEl = root.querySelector('[data-pyrene-np-success]');
      this.errorEl = root.querySelector('[data-pyrene-np-error]');
      this.closeBtn = root.querySelector('[data-pyrene-np-close]');
      this.emailInput = root.querySelector('#pyrene-np-email');

      this.brevoUrl = root.dataset.brevoUrl || '';
      this.delayDesktop = Number.isFinite(parseInt(root.dataset.delayDesktop, 10))
        ? parseInt(root.dataset.delayDesktop, 10)
        : 15;
      this.delayMobile = parseInt(root.dataset.delayMobile, 10) || 15;
      this.scrollDepth = parseInt(root.dataset.scrollDepth, 10) || 40;
      this.cooldownSuccess = parseInt(root.dataset.cooldownSuccess, 10) || 365;
      this.cooldownClose = parseInt(root.dataset.cooldownClose, 10) || 14;
      this.cooldownDismiss = parseInt(root.dataset.cooldownDismiss, 10) || 7;
      this.exitIntentEnabled = root.dataset.exitIntent === 'true';

      this.opened = false;
      this.scrollTriggerArmed = false;
      this.timerId = null;
      this.exitHandler = null;
      this.scrollHandler = null;

      this.init();
    }

    init() {
      // Pré-conditions : URL Brevo manquante ou page exclue ou cooldown actif
      if (!this.brevoUrl) {
        console.warn('[Pyrene Newsletter] URL Brevo non configurée, popup désactivé.');
        return;
      }
      if (isExcludedPath()) return;
      if (isCooldownActive()) return;

      this.armTriggers();
      this.bindUI();
    }

    armTriggers() {
      const mobile = isMobile();

      // Trigger desktop : exit-intent
      if (this.exitIntentEnabled && !mobile) {
        this.exitHandler = (e) => {
          if (e.clientY <= 0 && !this.opened) {
            this.open();
          }
        };
        document.addEventListener('mouseout', this.exitHandler);
      }

      // Trigger timer (desktop ET mobile, premier qui se déclenche gagne)
      const delay = mobile ? this.delayMobile : this.delayDesktop;
      if (delay > 0) {
        this.timerId = window.setTimeout(() => {
          if (!this.opened) this.open();
        }, delay * 1000);
      }

      // Trigger fallback : scroll depth
      this.scrollHandler = () => {
        if (this.opened || this.scrollTriggerArmed) return;
        const doc = document.documentElement;
        const scrolled = (window.scrollY + window.innerHeight) / doc.scrollHeight;
        if (scrolled * 100 >= this.scrollDepth) {
          this.scrollTriggerArmed = true;
          this.open();
        }
      };
      window.addEventListener('scroll', this.scrollHandler, { passive: true });
    }

    disarmTriggers() {
      if (this.exitHandler) {
        document.removeEventListener('mouseout', this.exitHandler);
        this.exitHandler = null;
      }
      if (this.timerId) {
        window.clearTimeout(this.timerId);
        this.timerId = null;
      }
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler);
        this.scrollHandler = null;
      }
    }

    bindUI() {
      this.closeBtn?.addEventListener('click', () => this.close('close'));
      this.form?.addEventListener('submit', (e) => this.onSubmit(e));

      // Fermeture sur Escape (le dialog le fait nativement mais on intercepte
      // pour distinguer "fermeture utilisateur" vs "fermeture programmée").
      this.dialog?.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close('dismiss');
      });

      // Clic sur le backdrop = clic sur le <dialog> hors du contenu interne
      this.dialog?.addEventListener('click', (e) => {
        const rect = this.dialog.getBoundingClientRect();
        const inside =
          e.clientX >= rect.left &&
          e.clientX <= rect.right &&
          e.clientY >= rect.top &&
          e.clientY <= rect.bottom;
        // Le dialog reçoit aussi les clics venant de l'intérieur via bubbling.
        // On filtre : si la cible est le dialog lui-même, c'est le backdrop.
        if (!inside || e.target === this.dialog) {
          this.close('dismiss');
        }
      });
    }

    open() {
      if (this.opened || !this.dialog) return;
      this.opened = true;
      this.root.hidden = false;

      try {
        if (typeof this.dialog.showModal === 'function') {
          this.dialog.showModal();
        } else {
          // Fallback navigateurs qui ne supportent pas <dialog>
          this.dialog.setAttribute('open', '');
        }
      } catch (_e) {
        this.dialog.setAttribute('open', '');
      }

      window.setTimeout(() => this.emailInput?.focus(), 50);
      this.disarmTriggers();
    }

    close(reason) {
      if (!this.opened || !this.dialog) return;
      this.opened = false;

      try {
        if (typeof this.dialog.close === 'function') {
          this.dialog.close();
        } else {
          this.dialog.removeAttribute('open');
        }
      } catch (_e) {
        this.dialog.removeAttribute('open');
      }

      this.root.hidden = true;

      // Sauvegarde du cooldown selon la raison
      if (reason === 'success') {
        writeState('subscribed', this.cooldownSuccess);
      } else if (reason === 'close') {
        writeState('closed', this.cooldownClose);
      } else if (reason === 'dismiss') {
        writeState('dismissed', this.cooldownDismiss);
      }
    }

    setLoading(loading) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = loading;
      this.submitBtn.dataset.loading = loading ? 'true' : 'false';
      if (this.emailInput) this.emailInput.readOnly = loading;
    }

    showSuccess() {
      this.form?.setAttribute('hidden', '');
      this.errorEl?.setAttribute('hidden', '');
      this.successEl?.removeAttribute('hidden');
      window.setTimeout(() => this.close('success'), 6000);
    }

    showError(message) {
      if (this.errorEl) {
        if (message) {
          const p = this.errorEl.querySelector('p');
          if (p) p.textContent = message;
        }
        this.errorEl.removeAttribute('hidden');
      }
    }

    clearError() {
      this.errorEl?.setAttribute('hidden', '');
    }

    async onSubmit(e) {
      e.preventDefault();
      if (!this.form || !this.brevoUrl) return;

      this.clearError();

      const formData = new FormData(this.form);
      const email = String(formData.get('EMAIL') || '').trim();
      const honeypot = String(formData.get('email_address_check') || '');
      const consent = formData.get('consent') === 'on';

      // Validation client
      if (!consent) {
        this.showError(this.errorEl?.dataset.msgConsent || 'Merci d\'accepter les conditions de la newsletter.');
        return;
      }
      if (!isValidEmail(email)) {
        this.showError(this.errorEl?.dataset.msgEmail || 'Merci de renseigner une adresse email valide.');
        this.emailInput?.setAttribute('aria-invalid', 'true');
        this.emailInput?.focus();
        return;
      }
      this.emailInput?.removeAttribute('aria-invalid');

      // Honeypot rempli : on simule un succès sans rien envoyer (bot)
      if (honeypot) {
        this.showSuccess();
        return;
      }

      this.setLoading(true);

      try {
        await submitToBrevo(this.brevoUrl, email, 'popup');
        this.showSuccess();
      } catch (err) {
        console.error('[Pyrene Newsletter] erreur fetch popup', err);
        this.showError();
      } finally {
        this.setLoading(false);
      }
    }
  }

  // === Footer form ===

  /**
   * Handler du formulaire newsletter présent dans le footer global.
   * Réutilise la même URL Brevo + même endpoint, avec SOURCE = "footer".
   * Plus simple que le popup (pas de cooldown, pas de déclencheurs, juste
   * un form classique).
   */
  class PyreneNewsletterFooter {
    /** @param {HTMLFormElement} form */
    constructor(form) {
      this.form = form;
      this.submitBtn = form.querySelector('[data-pyrene-footer-submit]');
      this.emailInput = form.querySelector('[data-pyrene-footer-email]');
      this.successEl = form.querySelector('[data-pyrene-footer-success]');
      this.errorEl = form.querySelector('[data-pyrene-footer-error]');
      this.brevoUrl = BREVO_FORM_URL;

      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    setLoading(loading) {
      if (!this.submitBtn) return;
      this.submitBtn.disabled = loading;
      this.submitBtn.dataset.loading = loading ? 'true' : 'false';
      if (this.emailInput) this.emailInput.readOnly = loading;
    }

    showSuccess() {
      this.errorEl?.setAttribute('hidden', '');
      this.successEl?.removeAttribute('hidden');
      // On masque le champ + bouton + RGPD pour ne laisser que le message
      this.form.querySelector('.pyrene-footer__newsletter-field')?.setAttribute('hidden', '');
      this.submitBtn?.setAttribute('hidden', '');
      this.form.querySelector('.pyrene-footer__newsletter-rgpd')?.setAttribute('hidden', '');
    }

    showError(message) {
      if (!this.errorEl) return;
      if (message) this.errorEl.textContent = message;
      this.errorEl.removeAttribute('hidden');
    }

    clearError() {
      this.errorEl?.setAttribute('hidden', '');
    }

    async onSubmit(e) {
      e.preventDefault();
      this.clearError();

      const formData = new FormData(this.form);
      const email = String(formData.get('EMAIL') || '').trim();
      const honeypot = String(formData.get('email_address_check') || '');

      if (!isValidEmail(email)) {
        this.showError(this.errorEl?.dataset.msgEmail || 'Merci de renseigner une adresse email valide.');
        this.emailInput?.setAttribute('aria-invalid', 'true');
        this.emailInput?.focus();
        return;
      }
      this.emailInput?.removeAttribute('aria-invalid');

      // Honeypot rempli → faux succès silencieux (bot)
      if (honeypot) {
        this.showSuccess();
        return;
      }

      this.setLoading(true);
      try {
        await submitToBrevo(this.brevoUrl, email, 'footer');
        this.showSuccess();
      } catch (err) {
        console.error('[Pyrene Newsletter] erreur fetch footer', err);
        this.showError();
      } finally {
        this.setLoading(false);
      }
    }
  }

  // === Bootstrap ===

  /**
   * Guard contre le double-init si le script est chargé plus d'une fois
   * (popup + footer chargent tous deux le même asset par sécurité).
   */
  const BOOTED_FLAG = 'pyreneNewsletterBooted';

  const boot = () => {
    const popupRoot = document.querySelector('[data-pyrene-newsletter-popup]');
    if (popupRoot && !popupRoot.dataset[BOOTED_FLAG]) {
      popupRoot.dataset[BOOTED_FLAG] = 'true';
      new PyreneNewsletterPopup(popupRoot);
    }

    const footerForm = document.querySelector('[data-pyrene-newsletter-footer]');
    if (footerForm && !footerForm.dataset[BOOTED_FLAG]) {
      footerForm.dataset[BOOTED_FLAG] = 'true';
      new PyreneNewsletterFooter(footerForm);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
